import { call, put } from 'redux-saga/effects';
import _get from 'lodash/get';

import {
    createAnswerAPI,
    deleteAnswerAPI,
    fetchAllAnswersAPI,
    fetchAnswerListAPI,
    fetchQuestionnaireInfoAPI,
    fetchQuestionnaireStatusAPI,
    fetchQuestionsAPI,
    setQuestionnaireStatusAPI,
    updateAnswerAPI,
    updateQuestionnaireStatusAPI,
    fetchQuestionnaireEligibilityAPI,
    fetchBrokerDetailsAPI,
    createConsentAPI,
    fetchConsentDetailsAPI,
    fetchCompanyAdditionalDataAPI,
    fetchPartnerConsentsListAPI,
    setACATaxYearInRedisAPI,
    fetchQuestionnaireDetailsAPI,
    createExpertServiceConsentAPI,
    fetchExpertServiceConsentsAPI,
    updateExpertServiceConsentAPI,
} from 'modules/apis';

import * as acaActions from '../action';
import { EXPERT_SERVICE_CONSENT } from '../constants';

/**
 * Saga to fetch QuestionnaireInfo
 *
 * @param   {Object}    action
 */
export function* fetchQuestionnaireInfoSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchQuestionnaireInfoAPI, { payload });
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchQuestionnaireInfo.success(data));
    } catch (e) {
        yield put(acaActions.fetchQuestionnaireInfo.error(e));
    }
}

/**
 * Saga to fetch Questionnaire Details
 *
 * @param   {Object}    action
 */
export function* fetchQuestionnaireDetailsSaga(action) {
    try {
        const {
            payload: { questionnaireId },
        } = action;
        const response = yield call(fetchQuestionnaireDetailsAPI, questionnaireId);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchQuestionnaireDetails.success(data));
    } catch (e) {
        yield put(acaActions.fetchQuestionnaireDetails.error(e));
    }
}

/**
 * Saga to fetch Questionnaire Status
 *
 * @param   {Object}    action
 */
export function* fetchQuestionnaireStatusSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchQuestionnaireStatusAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchQuestionnaireStatus.success(data));
    } catch (e) {
        yield put(acaActions.fetchQuestionnaireStatus.error(e));
    }
}

/**
 * Saga to set Questionnaire Status
 *
 * @param   {Object}    action
 */
export function* setQuestionnaireStatusSaga(action) {
    try {
        const {
            payload: { questionnaireId, data },
        } = action;
        const response = yield call(setQuestionnaireStatusAPI, { questionnaireId, data });
        yield put(acaActions.setQuestionnaireStatus.success(response));
    } catch (e) {
        yield put(acaActions.setQuestionnaireStatus.error(e));
    }
}

/**
 * Saga to update Questionnaire Status
 *
 * @param   {Object}    action
 */
export function* updateQuestionnaireStatusSaga(action) {
    try {
        const {
            payload: { questionnaireId, data, statusId },
        } = action;
        const response = yield call(updateQuestionnaireStatusAPI, {
            questionnaireId,
            data,
            statusId,
        });
        yield put(acaActions.updateQuestionnaireStatus.success(response));
    } catch (e) {
        yield put(acaActions.updateQuestionnaireStatus.error(e));
    }
}

/**
 * Saga to fetch all ACA reporting Questions
 *
 * @param   {Object}    action
 */
export function* fetchQuestionsSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchQuestionsAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchQuestions.success(data));
    } catch (e) {
        yield put(acaActions.fetchQuestions.error(e));
    }
}

/**
 * Saga to fetch answer status to a Question
 *
 * @param   {Object}    action
 */
export function* fetchAnswerListSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchAnswerListAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchAnswerList.success(data));
    } catch (e) {
        yield put(acaActions.fetchAnswerList.error(e));
    }
}

/**
 * Saga to create answer to a Question
 *
 * @param   {Object}    action
 */
export function* createAnswerSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(createAnswerAPI, payload);
        yield put(acaActions.createAnswer.success(response));
    } catch (e) {
        yield put(acaActions.createAnswer.error(e));
    }
}

/**
 * Saga to update answer to a Question
 *
 * @param   {Object}    action
 */
export function* updateAnswerSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(updateAnswerAPI, payload);
        yield put(acaActions.updateAnswer.success(response));
    } catch (e) {
        yield put(acaActions.updateAnswer.error(e));
    }
}

/**
 * Saga to delete answer api
 *
 * @param   {Object}    action
 */
export function* deleteAnswerSaga(action) {
    try {
        const {
            payload: { questionnaireId, questionId, answerId },
        } = action;
        const response = yield call(deleteAnswerAPI, { questionnaireId, questionId, answerId });
        yield put(acaActions.deleteAnswer.success(response));
    } catch (e) {
        yield put(acaActions.deleteAnswer.error(e));
    }
}

/**
 * Saga to fetch all answers of a questionnaire.
 *
 * @param   {Object}    action
 */
export function* fetchAllAnswersSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchAllAnswersAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchAllAnswers.success(data));
    } catch (e) {
        yield put(acaActions.fetchAllAnswers.error(e));
    }
}

/**
 * Saga to fetch all Eligible Questionnaire Years.
 *
 * @param   {Object}    action
 */
export function* fetchQuestionnaireEligibilitySaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchQuestionnaireEligibilityAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchQuestionnaireEligibility.success(data));
    } catch (e) {
        yield put(acaActions.fetchQuestionnaireEligibility.error(e));
    }
}

/**
 * Saga to fetch broker details.
 *
 * @param   {Object}    action
 */
export function* fetchBrokerDetailsSaga(action) {
    try {
        const {
            payload: { partnerId },
        } = action;
        const response = yield call(fetchBrokerDetailsAPI, partnerId);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchBrokerDetails.success(data));
    } catch (e) {
        yield put(acaActions.fetchBrokerDetails.error(e));
    }
}

/**
 * Saga to create Company Partner Consent
 *
 * @param   {Object}    action
 */
export function* createConsentSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(createConsentAPI, payload);
        yield put(acaActions.createConsent.success(response));
    } catch (e) {
        yield put(acaActions.createConsent.error(e));
    }
}

/**
 * Saga to fetch Company Partner consent details.
 *
 * @param   {Object}    action
 */
export function* fetchConsentDetailsSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchConsentDetailsAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchConsentDetails.success(data));
    } catch (e) {
        yield put(acaActions.fetchConsentDetails.error(e));
    }
}

/**
 * Saga to fetch Company additional data.
 *
 * @param   {Object}    action
 */
export function* fetchCompanyAdditionalDataSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchCompanyAdditionalDataAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchCompanyAdditionalData.success(data));
    } catch (e) {
        yield put(acaActions.fetchCompanyAdditionalData.error(e));
    }
}

/**
 * Saga to fetch Partner consents list.
 *
 * @param   {Object}    action
 */
export function* fetchPartnerConsentsListSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(fetchPartnerConsentsListAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.fetchPartnerConsentsList.success(data));
    } catch (e) {
        yield put(acaActions.fetchPartnerConsentsList.error(e));
    }
}

/**
 * Saga to set ACA tax year in Redis.
 *
 * @param   {Object}    action
 */
export function* setAcaTaxYearInRedisSaga(action) {
    try {
        const { payload } = action;
        const response = yield call(setACATaxYearInRedisAPI, payload);
        const data = _get(response, 'data', {});
        yield put(acaActions.setAcaTaxYearInRedis.success(data));
    } catch (e) {
        yield put(acaActions.setAcaTaxYearInRedis.error(e));
    }
}

/**
 * Saga to fetch Expert Service consents.
 *
 * @param   {Object}    action
 */
function* fetchExpertConsentSaga(action) {
    try {
        const {
            payload: { companyId, year, consentStatus },
        } = action;
        const years = [].concat(year).filter(y => y != null);
        const response = yield call(fetchExpertServiceConsentsAPI, {
            companyId,
            year: years,
            consentStatus,
        });
        const rows = _get(response, 'data.consent', response.data);
        const consents = Array.isArray(rows) ? rows : [];
        yield put(acaActions.fetchExpertConsent.success(consents, { companyId, years }));
    } catch (e) {
        yield put(acaActions.fetchExpertConsent.error(e));
    }
}

/**
 * Saga to create Expert Service consent.
 *
 * @param   {Object}    action
 */
function* createExpertConsentSaga(action) {
    try {
        const {
            payload: {
                companyId,
                data: { year, contactMethod },
            },
        } = action;
        const data = { year, contactMethod };
        const response = yield call(createExpertServiceConsentAPI, { companyId, data });
        yield put(
            acaActions.createExpertConsent.success(_get(response, 'data.consent', response.data), {
                companyId,
            })
        );
    } catch (e) {
        yield put(acaActions.createExpertConsent.error(e));
    }
}

/**
 * Saga to update Expert Service consent.
 *
 * @param   {Object}    action
 */
function* updateExpertConsentSaga(action) {
    try {
        const {
            payload: { companyId, consentId },
        } = action;
        const data = { consentStatus: EXPERT_SERVICE_CONSENT.STATUS.OPT_OUT };
        const response = yield call(updateExpertServiceConsentAPI, {
            companyId,
            consentId,
            data,
        });
        yield put(
            acaActions.updateExpertConsent.success(_get(response, 'data.consent', response.data), {
                companyId,
                consentId,
            })
        );
    } catch (e) {
        yield put(acaActions.updateExpertConsent.error(e));
    }
}

export { createExpertConsentSaga, fetchExpertConsentSaga, updateExpertConsentSaga };
